import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getRandomInspirationalQuote } from '../constants/inspirationalQuotes';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [loginInspiration, setLoginInspiration] = useState(null);
  const [skipLoadUser, setSkipLoadUser] = useState(false);

  const loadUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      const loadedUser = response.data.data.user;
      setUser({
        ...loadedUser,
        id: loadedUser.id || loadedUser._id
      });
    } catch (error) {
      console.error('Failed to load user:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token && !skipLoadUser) {
      loadUser();
    } else {
      setLoading(false);
      if (skipLoadUser) {
        setSkipLoadUser(false);
      }
    }
  }, [token, loadUser, skipLoadUser]);

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { token: newToken, user: userData } = response.data.data;
      
      localStorage.setItem('token', newToken);
      setSkipLoadUser(true); // Skip loadUser since we have fresh user data
      setToken(newToken);
      setUser({
        ...userData,
        id: userData.id || userData._id
      });
      setLoading(false); // Set loading to false immediately
      setLoginInspiration({
        quote: getRandomInspirationalQuote(),
        shownAt: Date.now()
      });
      
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data.data;
      
      localStorage.setItem('token', newToken);
      setSkipLoadUser(true); // Skip loadUser since we have fresh user data
      setToken(newToken);
      setUser({
        ...newUser,
        id: newUser.id || newUser._id
      });
      setLoading(false); // Set loading to false immediately
      
      return { success: true, user: newUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setLoginInspiration(null);
  };

  const dismissLoginInspiration = () => {
    setLoginInspiration(null);
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    loginInspiration,
    dismissLoginInspiration,
    refreshUser: loadUser,
    isAuthenticated: !!user,
    isClient: user?.role === 'client',
    isClinician: user?.role === 'clinician' || user?.role === 'clergy' || user?.role === 'coach',
    isClergy: user?.role === 'clergy',
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
